import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useApiQuery } from "app/modules/api-client";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";

import { useIndustryAvailabilityParams } from "../useIndustryAvailabilityParams";

export interface IIndustryCampaignRow {
    campaignName: string;
    landingPageLink: string;
    advertiser: string;
    creativesCount: number;
    activeDays: number;
    firstSeen: string;
    lastSeen: string;
    campaignId: string;
}

// Server-side paginated/searched/sorted. `search` (campaign name) and `sort`/`asc`
// are forwarded to the endpoint; `sort` must be a server key (snake_case, e.g.
// `last_seen`) — the caller adapts its column keys before passing them in.
export const useIndustryTopCampaigns = (
    category: IFlattenedCategory,
    page: number,
    pageSize: number,
    search?: string,
    sort?: string,
    asc?: boolean,
    advertisers?: string[],
) => {
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryTopCampaignsList,
    );

    const { data, isLoading, isFetching, isError } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName: "adintelligenceIndustryTopCampaignsList",
            options: {
                enabled: !availability.isFullyUnavailable,
                select: (response) => ({
                    rows: (response?.data ?? []).map((item) => ({
                        campaignName: item.campaign ?? "",
                        landingPageLink: item.landingPageLink ?? "",
                        advertiser: item.advertiser ?? "",
                        creativesCount: item.creativesCount,
                        activeDays: item.activeDays,
                        firstSeen: item.firstSeen ?? "",
                        lastSeen: item.lastSeen ?? "",
                        campaignId: item.campaignId ?? "",
                    })),
                    totalCount: response?.totalCount ?? 0,
                }),
            },
        },
        {
            ...params,
            page,
            pageSize,
            ...(search ? { search } : {}),
            ...(sort ? { sort, asc: !!asc } : {}),
            ...(advertisers?.length ? { advertisers } : {}),
        },
    );

    return {
        rows: data?.rows ?? [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        isError,
        availability,
    };
};
